/**
 * Pure arrow-routing math for the swimlane composer — verbatim ports from the
 * committed live view (`git show HEAD:packages/app-shell/src/process/
 * SwimlaneView.tsx`, lines 2426-2653) and its React-free `flow-graph.ts`
 * helpers (`polyPathLength` / `polyPathPointAt` / `roundedPolyPath` /
 * `commentLabel`). Everything here consumes plain coordinates — no DOM, no
 * React — so the headless composer and (in P5) the live overlay share ONE
 * routing implementation.
 */

import type { SwimlaneGeometry } from "./swimlane-geometry.js";
import { BLOCK_INSET, LANE_WIDTH, ROW_MIN_H } from "./swimlane-metrics.js";

// ─────────────────────────────────────────────────────────────────────────────
// Block bounds + obstacles (the committed blockYBounds / obstaclesFromGeom,
// re-expressed over the pure SwimlaneGeometry — canvas coords throughout)
// ─────────────────────────────────────────────────────────────────────────────

export interface BlockBounds {
  readonly blockTop: number;
  readonly blockBottom: number;
  readonly blockCy: number;
}

/** A block's vertical bounds: the measured (pure) block rect when the cell
 *  holds one, else the row's padded region — the committed fallback for
 *  gateway rows / lane −1. */
export function blockBounds(geom: SwimlaneGeometry, y: number, laneIdx: number): BlockBounds {
  const b = geom.blocks.get(`${y}:${laneIdx}`);
  if (b) {
    return { blockTop: b.rect.y, blockBottom: b.rect.y + b.rect.h, blockCy: b.cy };
  }
  const top = geom.rowTops[y] ?? geom.headerH;
  const h = geom.rowHeights[y] ?? geom.metrics.ROW_MIN_H;
  const blockTop = top + (geom.rowTopPads[y] ?? 8);
  const blockBottom = top + h - (geom.rowBottomPads[y] ?? 8);
  return { blockTop, blockBottom, blockCy: (blockTop + blockBottom) / 2 };
}

export interface Obstacle {
  readonly key: string;
  readonly xl: number;
  readonly xr: number;
  readonly yt: number;
  readonly yb: number;
}

/** Every block as a routing obstacle. The committed pass derives x from the
 *  lane (all blocks share the lane's inset width) — kept, so the routing is
 *  byte-faithful even for narrow terminators. Unanchored PROSE cells are
 *  excluded: the committed CommentCell only registers its ref when anchored
 *  (SwimlaneView 2004), so a full-width prose row is invisible to the
 *  optimiser (terminator/do rows at lane −1 DO register — committed quirk). */
export function obstaclesFromGeometry(geom: SwimlaneGeometry): Obstacle[] {
  const out: Obstacle[] = [];
  const { LANE_WIDTH, BLOCK_INSET } = geom.metrics;
  for (const [key, b] of geom.blocks) {
    if (b.kind === "prose") continue;
    const lane = Number(key.slice(key.indexOf(":") + 1));
    if (!Number.isFinite(lane)) continue;
    const xl = lane * LANE_WIDTH + BLOCK_INSET;
    out.push({ key, xl, xr: xl + (LANE_WIDTH - BLOCK_INSET * 2), yt: b.rect.y, yb: b.rect.y + b.rect.h });
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Segments + crossing-minimised bridge assignment (committed 2474-2653)
// ─────────────────────────────────────────────────────────────────────────────

/** Axis-aligned segment. */
export interface Seg {
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
}

/** True when two axis-aligned segments cross (or collinearly overlap). */
export function segsCross(a: Seg, b: Seg): boolean {
  const aH = Math.abs(a.y1 - a.y2) < 0.5;
  const bH = Math.abs(b.y1 - b.y2) < 0.5;
  if (aH && !bH) {
    const axLo = Math.min(a.x1, a.x2), axHi = Math.max(a.x1, a.x2);
    const byLo = Math.min(b.y1, b.y2), byHi = Math.max(b.y1, b.y2);
    return b.x1 > axLo + 0.5 && b.x1 < axHi - 0.5 && a.y1 > byLo + 0.5 && a.y1 < byHi - 0.5;
  }
  if (!aH && bH) return segsCross(b, a);
  if (aH && bH) {
    if (Math.abs(a.y1 - b.y1) > 1) return false;
    const a1 = Math.min(a.x1, a.x2), a2 = Math.max(a.x1, a.x2);
    const b1 = Math.min(b.x1, b.x2), b2 = Math.max(b.x1, b.x2);
    return Math.min(a2, b2) > Math.max(a1, b1) + 1;
  }
  if (Math.abs(a.x1 - b.x1) > 1) return false;
  const a1 = Math.min(a.y1, a.y2), a2 = Math.max(a.y1, a.y2);
  const b1 = Math.min(b.y1, b.y2), b2 = Math.max(b.y1, b.y2);
  return Math.min(a2, b2) > Math.max(a1, b1) + 1;
}

/** The three segments of a bridge routed through horizontal level `by`. */
export function bridgeSegs(prevExitY: number, curEntryY: number, prevCx: number, targetCx: number, by: number): Seg[] {
  return [
    { x1: prevCx, y1: prevExitY, x2: prevCx, y2: by },
    { x1: prevCx, y1: by, x2: targetCx, y2: by },
    { x1: targetCx, y1: by, x2: targetCx, y2: curEntryY },
  ];
}

/** A cross-column arrow whose horizontal bridge Y is free to choose. */
export interface RouteArrow {
  readonly id: string;
  readonly exitY: number;
  readonly entryY: number;
  readonly prevCx: number;
  readonly targetCx: number;
  readonly selfKeys: ReadonlySet<string>;
}

const arrowMid = (a: RouteArrow): number => (a.exitY + a.entryY) / 2;

/** Candidate bridge Ys for an arrow: the midpoint plus the clear gaps just
 *  above/below every obstacle and fixed segment within its span. */
function arrowCandidates(a: RouteArrow, obstacles: readonly Obstacle[], fixed: readonly Seg[]): number[] {
  const yLo = Math.min(a.exitY, a.entryY);
  const yHi = Math.max(a.exitY, a.entryY);
  const set = new Set<number>([arrowMid(a)]);
  for (const o of obstacles) for (const y of [o.yt - 4, o.yb + 4]) if (y > yLo && y < yHi) set.add(y);
  for (const s of fixed) if (Math.abs(s.y1 - s.y2) < 0.5) for (const y of [s.y1 - 5, s.y1 + 5]) if (y > yLo && y < yHi) set.add(y);
  const edges = [yLo, ...[...set].filter((y) => y > yLo && y < yHi), yHi].sort((p, q) => p - q);
  for (let i = 1; i < edges.length; i++) {
    const mid = (edges[i - 1]! + edges[i]!) / 2;
    if (mid > yLo && mid < yHi) set.add(mid);
  }
  const span = yHi - yLo;
  if (span > 0) for (let k = 1; k < 8; k++) set.add(yLo + (span * k) / 8);
  return [...set];
}

/** Crossings of an arrow's bridge against blocks + fixed segments. */
function arrowStaticCost(a: RouteArrow, by: number, obstacles: readonly Obstacle[], fixed: readonly Seg[]): number {
  const xLo = Math.min(a.prevCx, a.targetCx), xHi = Math.max(a.prevCx, a.targetCx);
  let c = 0;
  for (const o of obstacles) {
    if (a.selfKeys.has(o.key)) continue;
    if (o.yt <= by && by <= o.yb && o.xr > xLo && o.xl < xHi) c += 1;
    const a1 = Math.min(a.exitY, by), b1 = Math.max(a.exitY, by);
    if (o.xl <= a.prevCx && a.prevCx <= o.xr && o.yb > a1 && o.yt < b1) c += 1;
    const a2 = Math.min(by, a.entryY), b2 = Math.max(by, a.entryY);
    if (o.xl <= a.targetCx && a.targetCx <= o.xr && o.yb > a2 && o.yt < b2) c += 1;
  }
  const segs = bridgeSegs(a.exitY, a.entryY, a.prevCx, a.targetCx, by);
  for (const f of fixed) for (const s of segs) if (segsCross(s, f)) c += 1;
  return c;
}

/** Crossings between two free arrows' bridges at the given Ys. */
function arrowPairCost(a: RouteArrow, ya: number, b: RouteArrow, yb: number): number {
  const sa = bridgeSegs(a.exitY, a.entryY, a.prevCx, a.targetCx, ya);
  const sb = bridgeSegs(b.exitY, b.entryY, b.prevCx, b.targetCx, yb);
  let c = 0;
  for (const x of sa) for (const y of sb) if (segsCross(x, y)) c += 1;
  return c;
}

/**
 * Choose every cross-column arrow's bridge Y to MINIMISE total crossings —
 * coordinate descent from deterministic seeds + a pairwise 2-opt polish.
 * Verbatim committed `optimizeBridges` (deterministic, order-independent).
 */
export function optimizeBridges(arrows: readonly RouteArrow[], obstacles: readonly Obstacle[], fixed: readonly Seg[]): Map<string, number> {
  const n = arrows.length;
  const out = new Map<string, number>();
  if (n === 0) return out;
  const cands = arrows.map((a) => arrowCandidates(a, obstacles, fixed));
  const staticCost = arrows.map((a, i) => {
    const m = new Map<number, number>();
    for (const y of cands[i]!) m.set(y, arrowStaticCost(a, y, obstacles, fixed));
    return m;
  });
  const energy = (asg: number[]): number => {
    let e = 0;
    for (let i = 0; i < n; i++) e += staticCost[i]!.get(asg[i]!)!;
    for (let i = 0; i < n; i++) for (let j = i + 1; j < n; j++) e += arrowPairCost(arrows[i]!, asg[i]!, arrows[j]!, asg[j]!);
    return e;
  };
  const descend = (asg: number[]): number[] => {
    for (let guard = 0; guard < 24; guard++) {
      let moved = false;
      for (let i = 0; i < n; i++) {
        let bestY = asg[i]!, bestC = Infinity;
        for (const y of cands[i]!) {
          let c = staticCost[i]!.get(y)!;
          for (let j = 0; j < n; j++) if (j !== i) c += arrowPairCost(arrows[i]!, y, arrows[j]!, asg[j]!);
          c += Math.abs(y - arrowMid(arrows[i]!)) / 1e5;
          if (c < bestC) { bestC = c; bestY = y; }
        }
        if (bestY !== asg[i]) { asg[i] = bestY; moved = true; }
      }
      if (!moved) break;
    }
    return asg;
  };
  const seeds: number[][] = [];
  seeds.push(arrows.map((_, i) => {
    let by = cands[i]![0]!, bc = Infinity;
    for (const y of cands[i]!) { const c = staticCost[i]!.get(y)!; if (c < bc) { bc = c; by = y; } }
    return by;
  }));
  for (let r = 1; r <= 10; r++) seeds.push(arrows.map((_, i) => cands[i]![(r * 7 + i * 5 + r * i) % cands[i]!.length]!));
  let best = descend(seeds[0]!.slice());
  let bestE = energy(best);
  for (let s = 1; s < seeds.length && bestE > 0; s++) {
    const res = descend(seeds[s]!.slice());
    const e = energy(res);
    if (e < bestE) { bestE = e; best = res; }
  }
  const otherCost = (i: number, y: number, skip: number) => {
    let c = staticCost[i]!.get(y)!;
    for (let k = 0; k < n; k++) if (k !== i && k !== skip) c += arrowPairCost(arrows[i]!, y, arrows[k]!, best[k]!);
    return c;
  };
  for (let pass = 0; pass < 6 && bestE > 0; pass++) {
    let changed = false;
    for (let i = 0; i < n; i++) for (let j = i + 1; j < n; j++) {
      if (arrowPairCost(arrows[i]!, best[i]!, arrows[j]!, best[j]!) === 0) continue;
      let bi = best[i]!, bj = best[j]!, bc = Infinity;
      for (const yi of cands[i]!) for (const yj of cands[j]!) {
        const c = otherCost(i, yi, j) + otherCost(j, yj, i) + arrowPairCost(arrows[i]!, yi, arrows[j]!, yj)
          + (Math.abs(yi - arrowMid(arrows[i]!)) + Math.abs(yj - arrowMid(arrows[j]!))) / 1e5;
        if (c < bc) { bc = c; bi = yi; bj = yj; }
      }
      if (bi !== best[i] || bj !== best[j]) { best[i] = bi; best[j] = bj; changed = true; }
    }
    if (!changed) break;
    bestE = energy(best);
  }
  arrows.forEach((a, i) => out.set(a.id, best[i]!));
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Poly-path helpers + comment labels (committed flow-graph.ts / SwimlaneView)
// ─────────────────────────────────────────────────────────────────────────────

/** Total length of an orthogonal M/L poly-path (sum of segment lengths). */
export function polyPathLength(d: string): number {
  const pts = (d.match(/[ML]\s*[-\d.]+\s+[-\d.]+/g) ?? []).map((c) => {
    const n = c.slice(1).trim().split(/\s+/).map(Number);
    return [n[0]!, n[1]!] as const;
  });
  let len = 0;
  for (let i = 1; i < pts.length; i++) len += Math.hypot(pts[i]![0] - pts[i - 1]![0], pts[i]![1] - pts[i - 1]![1]);
  return len;
}

/** The point at fraction `frac` (0..1) of an orthogonal poly-path's length —
 *  places an edge's midpoint label (`frac = 0.5`). Null for an empty path. */
export function polyPathPointAt(d: string, frac: number): [number, number] | null {
  const pts = (d.match(/[ML]\s*[-\d.]+\s+[-\d.]+/g) ?? []).map((c) => {
    const n = c.slice(1).trim().split(/\s+/).map(Number);
    return [n[0]!, n[1]!] as [number, number];
  });
  if (pts.length === 0) return null;
  if (pts.length === 1) return pts[0]!;
  const total = polyPathLength(d);
  if (total === 0) return pts[0]!;
  let target = total * Math.max(0, Math.min(1, frac));
  for (let i = 1; i < pts.length; i++) {
    const seg = Math.hypot(pts[i]![0] - pts[i - 1]![0], pts[i]![1] - pts[i - 1]![1]);
    if (target <= seg || i === pts.length - 1) {
      const t = seg === 0 ? 0 : target / seg;
      return [pts[i - 1]![0] + (pts[i]![0] - pts[i - 1]![0]) * t, pts[i - 1]![1] + (pts[i]![1] - pts[i - 1]![1]) * t];
    }
    target -= seg;
  }
  return pts[pts.length - 1]!;
}

/**
 * SVG path for a polygon with rounded corners: each vertex is replaced by a
 * quadratic arc of radius `r` (clamped to half the shorter adjacent edge).
 * Coordinates are rounded to 2dp at generation — the renderer's portability
 * contract — so gateway triangles / re-entry arcs never leak raw floats.
 */
export function roundedPolyPath(pts: readonly [number, number][], r: number): string {
  const r2 = (n: number): number => Math.round(n * 100) / 100;
  const n = pts.length;
  const unit = (a: [number, number], b: [number, number]): [number, number] => {
    const dx = b[0] - a[0], dy = b[1] - a[1];
    const len = Math.hypot(dx, dy) || 1;
    return [dx / len, dy / len];
  };
  let d = "";
  for (let i = 0; i < n; i++) {
    const cur = pts[i]!, prev = pts[(i - 1 + n) % n]!, next = pts[(i + 1) % n]!;
    const e1 = Math.hypot(prev[0] - cur[0], prev[1] - cur[1]);
    const e2 = Math.hypot(next[0] - cur[0], next[1] - cur[1]);
    const rr = Math.min(r, e1 / 2, e2 / 2);
    const v1 = unit(cur, prev), v2 = unit(cur, next);
    const p1: [number, number] = [cur[0] + v1[0] * rr, cur[1] + v1[1] * rr];
    const p2: [number, number] = [cur[0] + v2[0] * rr, cur[1] + v2[1] * rr];
    d += `${i === 0 ? "M" : " L"} ${r2(p1[0])} ${r2(p1[1])} Q ${r2(cur[0])} ${r2(cur[1])} ${r2(p2[0])} ${r2(p2[1])}`;
  }
  return d + " Z";
}

/**
 * Open-polyline variant of {@link roundedPolyPath}: endpoints stay EXACT
 * (no corner arc, no closing `Z`), only interior vertices round. For arcs
 * that start/end on a block edge — the closed variant strokes a spurious
 * diagonal back to the start (the committed seq re-entry bug, fixed in P5).
 */
export function roundedPolylinePath(pts: readonly [number, number][], r: number): string {
  const r2 = (n: number): number => Math.round(n * 100) / 100;
  const n = pts.length;
  if (n === 0) return "";
  if (n === 1) return `M ${r2(pts[0]![0])} ${r2(pts[0]![1])}`;
  const unit = (a: [number, number], b: [number, number]): [number, number] => {
    const dx = b[0] - a[0], dy = b[1] - a[1];
    const len = Math.hypot(dx, dy) || 1;
    return [dx / len, dy / len];
  };
  let d = `M ${r2(pts[0]![0])} ${r2(pts[0]![1])}`;
  for (let i = 1; i < n - 1; i++) {
    const cur = pts[i]!, prev = pts[i - 1]!, next = pts[i + 1]!;
    const e1 = Math.hypot(prev[0] - cur[0], prev[1] - cur[1]);
    const e2 = Math.hypot(next[0] - cur[0], next[1] - cur[1]);
    const rr = Math.min(r, e1 / 2, e2 / 2);
    const v1 = unit(cur, prev), v2 = unit(cur, next);
    const p1: [number, number] = [cur[0] + v1[0] * rr, cur[1] + v1[1] * rr];
    const p2: [number, number] = [cur[0] + v2[0] * rr, cur[1] + v2[1] * rr];
    d += ` L ${r2(p1[0])} ${r2(p1[1])} Q ${r2(cur[0])} ${r2(cur[1])} ${r2(p2[0])} ${r2(p2[1])}`;
  }
  d += ` L ${r2(pts[n - 1]![0])} ${r2(pts[n - 1]![1])}`;
  return d;
}

/** Visual width of a code point: East-Asian Wide / Fullwidth / emoji count as
 *  2, everything else as 1 — keeps the label cap a VISUAL budget. */
function glyphWidth(cp: number): number {
  return (cp >= 0x1100 && cp <= 0x115f) || (cp >= 0x2e80 && cp <= 0x303e) ||
    (cp >= 0x3041 && cp <= 0x33ff) || (cp >= 0x3400 && cp <= 0x4dbf) ||
    (cp >= 0x4e00 && cp <= 0x9fff) || (cp >= 0xa000 && cp <= 0xa4cf) ||
    (cp >= 0xac00 && cp <= 0xd7a3) || (cp >= 0xf900 && cp <= 0xfaff) ||
    (cp >= 0xfe30 && cp <= 0xfe4f) || (cp >= 0xff00 && cp <= 0xff60) ||
    (cp >= 0xffe0 && cp <= 0xffe6) || (cp >= 0x1f000 && cp <= 0x1faff) ||
    (cp >= 0x20000 && cp <= 0x3fffd)
    ? 2 : 1;
}

import { COMMENT_LABEL_MAX } from "./swimlane-metrics.js";

/** Collapse a freehand call comment to a single-line edge label, ellipsed to a
 *  ~one-lane-width budget. Truncates by CODE POINT (never splits a surrogate
 *  pair) on a visual-width budget (wide glyphs = 2). */
export function commentLabel(s: string): string {
  const one = s.replace(/\s+/g, " ").trim();
  const chars = [...one];
  let total = 0;
  for (const ch of chars) total += glyphWidth(ch.codePointAt(0)!);
  if (total <= COMMENT_LABEL_MAX) return one;
  let out = "";
  let w = 0;
  for (const ch of chars) {
    const cw = glyphWidth(ch.codePointAt(0)!);
    if (w + cw > COMMENT_LABEL_MAX - 1) break;
    out += ch;
    w += cw;
  }
  return `${out}…`;
}
